import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { jobsOnly, useSession } from '../../session'
import { CircleAlert, ClipboardList, FilePlus2, FileText, Plus, Receipt, RotateCcw, Wallet } from 'lucide-react'
import { listCustomerQuotes, listOpenCustomerInvoices, listSales, scanDrafts } from '../../api/sales'
import { companyNow, fmtDateTime, fmtDay, parseWallClock, startOfToday } from '../../utils/format'
import Button from '../../components/Button'
import { Card, Tile } from '../../components/Summary'
import SalesTabs from './SalesTabs'
import { money, num, saleNo, statusOf as status, ymd as isoDay } from './salesShared'

// The Sales summary, after PropertyBuk's Finance summary: this month's
// numbers, then short lists of recent sales, unpaid invoices and quotes
// waiting on a reply. The tabs above lead to the full lists.

export default function SalesMenu({ roles = [] }) {
  const { has } = useSession()
  const canPOS = has('pos') && roles.some((r) => ['sales', 'admin', 'cashier', 'pos'].includes(String(r).trim().toLowerCase()))
  const canInvoice = has('invoices')  // quotes and invoices not from a job
  const hasDocs = has('invoices|jobs')
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    const today = companyNow()
    const start = isoDay(new Date(today.getFullYear(), today.getMonth(), 1))
    const end = isoDay(today)
    const quiet = (p, fallback) => p.catch(() => fallback)
    Promise.all([
      quiet(listSales({ start, end }), []),
      quiet(listOpenCustomerInvoices(), []),
      quiet(listCustomerQuotes({ status: 'Sent' }), []),
      quiet(scanDrafts({ start, end }), {}),
    ]).then(([sales, invoices, quotes, drafts]) => {
      if (cancelled) return
      setData({
        sales: Array.isArray(sales) ? sales : [],
        invoices: Array.isArray(invoices) ? invoices : [],
        quotes: Array.isArray(quotes) ? quotes : [],
        unfinishedRefunds: num(drafts?.refund_count),
      })
    })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="section-page">
      <SalesTabs />
      <header className="section-header">
        <h1>Sales</h1>
        <div className="section-actions">
          {data?.unfinishedRefunds > 0 && (
            <Link to="/sales/refund-lookup" className="section-chip">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {data.unfinishedRefunds} unfinished {data.unfinishedRefunds === 1 ? 'refund' : 'refunds'}
            </Link>
          )}
          {canInvoice && <Button as={Link} to="/sales/quotes/new" variant="outline" size="sm"><FilePlus2 size={14} />New quote</Button>}
          {canInvoice && <Button as={Link} to="/sales/customer-invoices/new" variant="outline" size="sm"><FileText size={14} />New invoice</Button>}
          {canPOS && <Button as={Link} to="/sales/pos" variant="primary" size="sm"><Plus size={14} />New sale</Button>}
        </div>
      </header>

      {data ? <Summary {...data} /> : <p className="sum-state">Loading sales…</p>}
    </div>
  )
}

function Summary({ sales, invoices, quotes }) {
  const { has, modules } = useSession()
  const quotesHere = has('invoices|jobs') && !jobsOnly(modules)  // otherwise quotes are on each job
  const hasDocs = has('invoices|jobs')  // quotes and invoices, including job ones
  const today = startOfToday()
  const month = today.toLocaleDateString(undefined, { month: 'long' })

  const posted = sales.filter((r) => status(r) === 'posted')
  const refunds = sales.filter((r) => status(r) === 'refunded')
  const revenue = posted.reduce((sum, r) => sum + num(r.revenue ?? r.total_amount), 0)
  const refunded = refunds.reduce((sum, r) => sum + Math.abs(num(r.total_amount ?? r.revenue)), 0)
  const recent = [...sales]
    .sort((a, b) => ((parseWallClock(b.sale_datetime)?.getTime() || 0) - (parseWallClock(a.sale_datetime)?.getTime() || 0)) || num(b.sale_id) - num(a.sale_id))
    .slice(0, 8)

  const owed = invoices.reduce((sum, r) => sum + num(r.balance_amount), 0)
  const isOverdue = (r) => {
    const due = parseWallClock(r.due_date)
    return !!due && due < today
  }
  const overdue = invoices.filter(isOverdue).reduce((sum, r) => sum + num(r.balance_amount), 0)

  const waiting = quotes.filter((q) => {
    const until = parseWallClock(q.valid_until)
    return !until || until >= today
  })
  const expired = quotes.length - waiting.length
  const waitingTotal = waiting.reduce((sum, q) => sum + num(q.total_amount), 0)

  return (
    <>
      <div className="sum-tiles">
        {has('pos') && (
        <Tile label={`Sales in ${month}`} href="/sales/list" icon={Receipt} value={money(revenue)}
          note={posted.length ? `${posted.length} ${posted.length === 1 ? 'sale' : 'sales'} · average ${money(revenue / posted.length)}` : 'No sales yet this month'} />
        )}
        {has('pos') && (
        <Tile label={`Refunds in ${month}`} href="/sales/refund-lookup" icon={RotateCcw} value={money(refunded)}
          note={refunds.length ? `${refunds.length} ${refunds.length === 1 ? 'refund' : 'refunds'}` : 'None this month'} />
        )}
        {hasDocs && (
        <Tile label="Owed by customers" href="/sales/customer-payments" icon={Wallet} value={money(owed)}
          note={overdue > 0 ? `${money(overdue)} overdue` : 'Nothing overdue'} tone={overdue > 0 ? 'warn' : undefined} />
        )}
        {quotesHere && (
        <Tile label="Quotes awaiting a reply" href="/sales/quotes" icon={ClipboardList} value={String(waiting.length)}
          note={waiting.length ? `Worth ${money(waitingTotal)}${expired ? ` · ${expired} expired` : ''}` : (expired ? `${expired} expired` : 'None waiting')} />
        )}
      </div>

      <div className="sum-columns">
        <div className="sum-main">
        {has('pos') && (
          <Card title="Recent sales" action={<Link to="/sales/list">All sales</Link>}>
            {recent.length ? (
              <ul className="sum-list">
                {recent.map((r) => {
                  const refund = status(r) === 'refunded'
                  const details = [r.payment_method, r.location_name].filter(Boolean).join(' · ')
                  return (
                    <li key={r.sale_id}>
                      <span className="sum-list-main">
                        <Link to={`/sales/detail/${r.sale_id}`}>{saleNo(r)}</Link>
                        {details && <span className="sum-muted"> · {details}</span>}
                        {!refund && status(r) !== 'posted' && <span className="sum-warn"> · {r.status || 'Draft'}</span>}
                      </span>
                      <span className="sum-list-side">
                        <span className="sum-muted">{fmtDateTime(r.sale_datetime)} · </span>
                        <span className={refund ? 'sum-danger' : undefined}>
                          {refund ? '−' : ''}{money(Math.abs(num(r.total_amount ?? r.revenue)))}
                        </span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="sum-muted">No sales yet this month.</p>
            )}
          </Card>
        )}
        </div>

        <div className="sum-side">
        {hasDocs && (
          <Card title="Unpaid invoices" action={<Link to="/sales/customer-payments">Record a payment</Link>}>
            {invoices.length ? (
              <ul className="sum-list">
                {invoices.slice(0, 5).map((r) => {
                  const due = parseWallClock(r.due_date)
                  return (
                    <li key={r.invoice_id}>
                      <span className="sum-list-main">
                        <Link to={`/sales/customer-invoices/${r.invoice_id}`}>{r.customer_name || r.invoice_number || 'Invoice'}</Link>
                        {due && (
                          <span className={isOverdue(r) ? 'sum-warn' : 'sum-muted'}>
                            {isOverdue(r) ? ` · overdue since ${fmtDay(due, { year: false })}` : ` · due ${fmtDay(due, { year: false })}`}
                          </span>
                        )}
                      </span>
                      <span className="sum-list-side">
                        {num(r.paid_to_date) + num(r.credited_to_date) > 0.004 && <span className="sum-muted">part paid · </span>}
                        {money(r.balance_amount)}
                      </span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="sum-muted">Every invoice is paid.</p>
            )}
            {invoices.length > 5 && (
              <p className="sum-more"><Link to="/sales/customer-invoices">{invoices.length - 5} more</Link></p>
            )}
          </Card>
        )}

        {quotesHere && (
          <Card title="Quotes awaiting a reply" action={<Link to="/sales/quotes">All quotes</Link>}>
            {waiting.length ? (
              <ul className="sum-list">
                {waiting.slice(0, 5).map((q) => {
                  const until = parseWallClock(q.valid_until)
                  return (
                    <li key={q.quote_id}>
                      <span className="sum-list-main">
                        <Link to={`/sales/quotes/${q.quote_id}`}>{q.customer_name || q.quote_number || 'Quote'}</Link>
                        {until && <span className="sum-muted"> · valid until {fmtDay(until, { year: false })}</span>}
                      </span>
                      <span className="sum-list-side">{money(q.total_amount)}</span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="sum-muted">No quotes are waiting on a customer.</p>
            )}
          </Card>
        )}
        </div>
      </div>
    </>
  )
}
